<template>
  <b-overlay :show="pageBusy" variant="transparent">
    <b-card>
      <div class="row pt-0">
        <div class="col-12 pb-1">
          <span class="font-weight-bolder">Filters</span>
        </div>
        <div class="col-lg-3 col-12 col-md-6">
          <label>Date Range</label>
          <flat-pickr
            v-model="range"
            class="form-control"
            :config="{ mode: 'range' }"
          />
        </div>
        <div class="col-lg-3 col-12 col-md-6">
          <label>Audience</label>
          <v-select
            v-model="audience"
            :reduce="e => e.value"
            :searchable="false"
            :options="audienceOptions"
            :clearable="false"
          />
        </div>
        <div class="col-lg-3 col-12 col-md-6">
          <label>Search</label>
          <input
            v-model="search"
            type="text"
            class="form-control"
            placeholder="Name, email, or organization"
          >
        </div>
        <div class="col-lg-3 col-12 col-md-6 pt-2 pt-lg-2" @click="filterResponses()">
          <span class="btn btn-primary">
            <feather-icon icon="SearchIcon" /> Search
          </span>
        </div>
      </div>
    </b-card>

    <b-card>
      <b-overlay :show="itemBusy">
        <div class="row">
          <div class="text-right col-12 pb-2">
            <span
              v-if="$can('export', 'Surveys') || $can('manage', 'Surveys') || $can('manage', 'all')"
              class="btn btn-success"
              @click="exportCsv()"
            >
              <feather-icon icon="DownloadIcon" /> Export CSV
            </span>
          </div>
        </div>

        <b-table
          class="rounded border"
          :items="items"
          :fields="fields"
          :per-page="10"
          :current-page="1"
          responsive
        >
          <template #head()="scope">
            <div class="text-nowrap text-primary">
              {{ scope.label }}
            </div>
          </template>

          <template #cell(audience)="data">
            <b-badge v-if="data.item._id" :variant="data.value === 'charity' ? 'primary' : 'info'">
              {{ data.value === 'charity' ? 'Charity' : 'Donor' }}
            </b-badge>
          </template>

          <template #cell(contact)="data">
            <div v-if="data.item._id" class="pt-1">
              <div class="font-weight-bolder">{{ (data.item.contact && data.item.contact.name) || '—' }}</div>
              <div class="text-muted">{{ (data.item.contact && data.item.contact.email) || 'No email' }}</div>
              <div v-if="data.item.contact && data.item.contact.organizationName" class="text-muted">
                {{ data.item.contact.organizationName }}
              </div>
            </div>
          </template>

          <template #cell(consent)="data">
            <b-badge v-if="data.item._id" :variant="data.value ? 'success' : 'secondary'">
              {{ data.value ? 'Yes' : 'No' }}
            </b-badge>
          </template>

          <template #cell(createdAt)="data">
            <div v-if="data.item._id" class="text-nowrap pt-1">
              {{ dayjs(data.value).format('MMM D, YYYY h:mm A') }}
            </div>
          </template>

          <template #cell(actions)="data">
            <div v-if="data.item._id" class="d-flex align-items-center">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-50"
                @click="openDetail(data.item)"
              >
                View
              </b-button>
              <b-button
                v-if="$can('delete', 'Surveys') || $can('manage', 'Surveys') || $can('manage', 'all')"
                size="sm"
                variant="outline-danger"
                @click="confirmDelete(data.item)"
              >
                Delete
              </b-button>
            </div>
          </template>
        </b-table>
      </b-overlay>

      <div class="d-flex p-1">
        <div class="w-100">
          Page <span class="text-primary">{{ currentPage }}</span> of
          <span class="text-primary">{{ Math.ceil(totalRows / 10) || 1 }}</span>
        </div>
        <div class="w-100">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="10"
            aria-controls="surveys-table"
            align="right"
            size="sm"
            class="my-0"
            @page-click="loadNewPage"
          />
        </div>
      </div>
    </b-card>

    <b-modal
      id="survey-detail-modal"
      v-model="showDetail"
      title="Survey response"
      size="lg"
      ok-only
      ok-title="Close"
      centered
      scrollable
      body-class="survey-detail-body"
    >
      <div v-if="selected">
        <div class="mb-2">
          <b-badge :variant="selected.audience === 'charity' ? 'primary' : 'info'">
            {{ selected.audience === 'charity' ? 'Charity' : 'Donor' }}
          </b-badge>
          <span class="ml-1 text-muted">
            Submitted {{ dayjs(selected.createdAt).format('MMM D, YYYY h:mm A') }}
          </span>
        </div>

        <h5 class="mt-2 mb-1">Contact</h5>
        <div class="survey-section mb-2">
          <div
            v-for="field in readableContact"
            :key="field.label"
            class="survey-field"
          >
            <div class="survey-field-label">{{ field.label }}</div>
            <div class="survey-field-value">{{ field.value }}</div>
          </div>
          <div class="survey-field">
            <div class="survey-field-label">Consent to contact</div>
            <div class="survey-field-value">{{ selected.consent ? 'Yes' : 'No' }}</div>
          </div>
        </div>

        <h5 class="mt-2 mb-1">Answers</h5>
        <div class="survey-section">
          <div
            v-for="item in readableAnswers"
            :key="item.key"
            class="survey-qa"
          >
            <div class="survey-q">
              <span class="survey-q-num">Q{{ item.number }}.</span>
              {{ item.question }}
            </div>
            <div class="survey-a" :class="{ 'survey-a--multiline': item.isMultiline }">
              {{ item.answer }}
            </div>
          </div>
          <div v-if="!readableAnswers.length" class="text-muted">
            No answers recorded.
          </div>
        </div>
      </div>
    </b-modal>
  </b-overlay>
</template>

<script>
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import dayjs from 'dayjs'
import flatPickr from 'vue-flatpickr-component'
import Utils from '@/utils/index'
import API from './api'
import {
  buildReadableAnswers,
  buildReadableContact,
  flattenReadableAnswersForExport,
  getExportAnswerColumnLabels,
} from './survey-labels'

const toast = useToast()

export default {
  name: 'Surveys',
  components: {
    flatPickr,
    vSelect,
  },
  data() {
    return {
      dayjs,
      currentPage: 1,
      totalRows: 10,
      range: '',
      searchDate: '',
      search: '',
      audience: '',
      audienceOptions: [
        { label: 'All', value: '' },
        { label: 'Charity', value: 'charity' },
        { label: 'Donor', value: 'donor' },
      ],
      items: [],
      fields: [
        { key: 'actions', label: 'Actions' },
        { key: 'audience', label: 'Audience' },
        { key: 'contact', label: 'Contact' },
        { key: 'consent', label: 'Consent' },
        { key: 'createdAt', label: 'Submitted' },
      ],
      searchParams: {},
      itemBusy: false,
      pageBusy: false,
      showDetail: false,
      selected: null,
      exportRows: [],
    }
  },
  computed: {
    readableAnswers() {
      return buildReadableAnswers(this.selected)
    },
    readableContact() {
      return buildReadableContact((this.selected && this.selected.contact) || {})
    },
  },
  watch: {
    range(newRange) {
      if (!newRange || typeof newRange !== 'string') {
        this.searchDate = ''
        return
      }
      const parts = newRange.split(' to ')
      if (parts.length === 2) {
        this.searchDate = { from: parts[0], to: parts[1] }
      } else if (parts[0] && parts[0].length === 10) {
        this.searchDate = { from: parts[0], to: parts[0] }
      } else {
        this.searchDate = ''
      }
    },
  },
  created() {
    this.items = Utils.completeTable()
    this.filterResponses()
  },
  methods: {
    loadNewPage(e, page) {
      e.preventDefault()
      this.getResponses(page)
    },
    getResponses(page = 1) {
      this.itemBusy = true
      API.getResponses({ ...this.searchParams, pageNum: page, pageSize: 10 }).then(res => {
        this.itemBusy = false
        if (res.success && res.data) {
          this.currentPage = page
          this.items = Utils.completeTable(res.data.list)
          this.totalRows = res.data.total || 10
        }
      }).catch(() => {
        this.itemBusy = false
        this.showToast()
      })
    },
    filterResponses() {
      this.searchParams = {}
      if (this.search) this.searchParams.search = this.search
      if (this.audience) this.searchParams.audience = this.audience
      if (this.searchDate && this.searchDate.from) {
        this.searchParams.startDate = `${this.searchDate.from} 00:00:00`
        const to = this.searchDate.to || this.searchDate.from
        this.searchParams.endDate = `${to} 23:59:59`
      }
      this.getResponses(1)
    },
    openDetail(item) {
      this.itemBusy = true
      API.getResponse(item._id).then(res => {
        this.itemBusy = false
        if (res.success && res.data) {
          const detail = res.data
          const listAnswers = (item && item.answers) || {}
          const detailAnswers = (detail && detail.answers) || {}
          this.selected = {
            ...item,
            ...detail,
            answers: {
              ...listAnswers,
              ...detailAnswers,
            },
            contact: {
              ...((item && item.contact) || {}),
              ...((detail && detail.contact) || {}),
            },
          }
          this.showDetail = true
        } else {
          this.showToast(res.message || 'Failed to load response')
        }
      }).catch(() => {
        this.itemBusy = false
        this.showToast()
      })
    },
    confirmDelete(item) {
      const contact = item.contact || {}
      const label = contact.email || contact.name || item._id
      this.$bvModal.msgBoxConfirm(
        'Delete this survey response from "' + label + '"?\n\nIt will be hidden from the backoffice list and CSV export, but kept in the database for audit purposes. This cannot be undone from the backoffice.',
        {
          title: 'Delete survey response',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          centered: true,
        },
      ).then(value => {
        if (value) {
          this.deleteResponse(item)
        }
      })
    },
    deleteResponse(item) {
      this.itemBusy = true
      API.deleteResponse(item._id).then(res => {
        this.itemBusy = false
        if (res.success) {
          if (this.selected && this.selected._id === item._id) {
            this.showDetail = false
            this.selected = null
          }
          this.showToast('Survey response deleted', 'CheckCircleIcon', 'success')
          this.getResponses(this.currentPage)
        } else {
          this.showToast(res.message || 'Failed to delete survey response')
        }
      }).catch(() => {
        this.itemBusy = false
        this.showToast()
      })
    },
    processExport(item) {
      const contact = item.contact || {}
      const answers = flattenReadableAnswersForExport(item)
      return {
        Audience: item.audience === 'charity' ? 'Charity' : 'Donor',
        Submitted: dayjs(item.createdAt).format('YYYY-MM-DD HH:mm'),
        Name: contact.name || '',
        Email: contact.email || '',
        Organization: contact.organizationName || '',
        Position: contact.position || '',
        Province: contact.province || '',
        'Consent to contact': item.consent ? 'Yes' : 'No',
        ...answers,
      }
    },
    exportCsv(page = 1) {
      API.exportResponses({ ...this.searchParams, pageNum: page, pageSize: 100 }).then(res => {
        if (res.success) {
          if (page === 1) {
            this.exportRows = res.data.list.map(e => this.processExport(e))
          } else {
            this.exportRows.push(...res.data.list.map(e => this.processExport(e)))
          }
          if (res.data.hasMore) {
            this.exportCsv(page + 1)
          } else if (!this.exportRows.length) {
            this.showToast('No responses to export', 'InfoIcon', 'info')
          } else {
            const csvContent = this.jsonToCsv(this.exportRows)
            this.downloadCsv('givelycf-survey-responses.csv', csvContent)
          }
        }
      }).catch(() => {
        this.showToast()
      })
    },
    jsonToCsv(jsonArray) {
      const baseHeaders = [
        'Audience',
        'Submitted',
        'Name',
        'Email',
        'Organization',
        'Position',
        'Province',
        'Consent to contact',
      ]
      const answerHeaders = getExportAnswerColumnLabels()
      const extraHeaders = []
      const seen = {}
      baseHeaders.concat(answerHeaders).forEach(function (header) {
        seen[header] = true
      })
      jsonArray.forEach(function (row) {
        Object.keys(row || {}).forEach(function (key) {
          if (!seen[key]) {
            seen[key] = true
            extraHeaders.push(key)
          }
        })
      })
      const headers = baseHeaders.concat(answerHeaders, extraHeaders)
      const rows = jsonArray.map(function (obj) {
        return headers.map(function (header) {
          const value = obj[header]
          return '"' + String(value == null ? '' : value).replace(/"/g, '""') + '"'
        }).join(',')
      })
      return [headers.map(function (header) {
        return '"' + String(header).replace(/"/g, '""') + '"'
      }).join(','), ...rows].join('\n')
    },
    downloadCsv(filename, content) {
      const blob = new Blob([content], { type: 'text/csv' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)
    },
    showToast(title = 'An error occurred', icon = 'AlertTriangleIcon', variant = 'danger') {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped>
.survey-section {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.02);
}

.survey-field + .survey-field,
.survey-qa + .survey-qa {
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.survey-field-label,
.survey-q {
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 0.25rem;
}

.survey-q-num {
  color: #ea5455;
  margin-right: 0.25rem;
}

.survey-field-value,
.survey-a {
  color: #4b4b4b;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
}

.survey-a--multiline {
  white-space: pre-line;
}
</style>
